import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { BellSlashIcon, CheckCircleIcon, FunnelIcon, TrayIcon } from "@phosphor-icons/react";

import { Badge, Button } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";

import {
  useNotifications,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useArchiveNotification,
} from "@/client/services/notification";
import { getEventDisplay, CATEGORY_LABELS } from "@/client/services/notification";
import type { NotificationItem } from "@/client/services/notification";
import type { NotificationCategory } from "@reactive-resume/dto";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  BOÎTE ADMIN — centre de notifications des opérateurs internes
 * ─────────────────────────────────────────────────────────────────────────────
 *  Objectif : qu'aucune demande (retrait, aide, paiement) ne soit jamais
 *  manquée. Filtres par catégorie + non-lues, groupement par jour, ouverture
 *  = acquittement (marqué lu) + navigation vers la file métier.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const ADMIN_CATEGORIES: (NotificationCategory | "all")[] = [
  "all",
  "system",
  "finance",
  "campaign",
  "account",
];

const relativeTime = (value: string | Date) => {
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  return new Date(value).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};

const dayLabel = (value: string | Date) => {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400000);
  if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (date.toDateString() === yesterday.toDateString()) return "Hier";
  return date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
};

export const InboxAdminPage = () => {
  const navigate = useNavigate();
  const [category, setCategory] = useState<NotificationCategory | "all">("all");
  const [unreadOnly, setUnreadOnly] = useState(false);

  const filters = {
    ...(category !== "all" ? { category } : {}),
    ...(unreadOnly ? { unreadOnly: true } : {}),
    limit: 50,
  };
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useNotifications(filters);

  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const archive = useArchiveNotification();

  const items = useMemo(
    () => (data?.pages ?? []).flatMap((page) => (page as { items: NotificationItem[] }).items),
    [data],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, NotificationItem[]>();
    for (const item of items) {
      const key = dayLabel(item.createdAt);
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    return [...map.entries()];
  }, [items]);

  const unreadTotal = items.filter((i) => !i.isRead).length;

  const open = (notification: NotificationItem) => {
    if (!notification.isRead) markAsRead.mutate(notification.id);
    if (notification.link) navigate(notification.link);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* En-tête */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-admin-text-primary">Boîte de réception</h1>
          <p className="mt-1 text-sm text-admin-text-secondary">
            {unreadTotal > 0
              ? `${unreadTotal} notification${unreadTotal > 1 ? "s" : ""} à traiter`
              : "Vous êtes à jour — aucune notification non lue"}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="border-admin-border bg-admin-background text-admin-text-secondary hover:text-admin-text-primary"
          onClick={() => markAllAsRead.mutate()}
          disabled={unreadTotal === 0}
        >
          <CheckCircleIcon size={16} className="mr-2" />
          Tout marquer comme lu
        </Button>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2">
        <FunnelIcon size={16} className="mr-1 text-admin-text-muted" />
        {ADMIN_CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
              category === c
                ? "border-admin-primary bg-admin-primary text-admin-primary-foreground"
                : "border-admin-border bg-admin-surface text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary",
            )}
          >
            {c === "all" ? "Tout" : CATEGORY_LABELS[c as NotificationCategory]}
          </button>
        ))}
        <button
          onClick={() => setUnreadOnly((v) => !v)}
          className={cn(
            "ml-auto flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
            unreadOnly
              ? "border-amber-500/50 bg-amber-500/10 text-amber-600"
              : "border-admin-border bg-admin-surface text-admin-text-secondary hover:text-admin-text-primary",
          )}
        >
          <BellSlashIcon size={14} />
          Non lues uniquement
        </button>
      </div>

      <NotificationList
        isLoading={isLoading}
        items={items}
        grouped={grouped}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onOpen={open}
        onArchive={(id) => archive.mutate({ id })}
        onLoadMore={() => fetchNextPage()}
      />
    </div>
  );
};

type ListProps = {
  isLoading: boolean;
  items: NotificationItem[];
  grouped: [string, NotificationItem[]][];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onOpen: (item: NotificationItem) => void;
  onArchive: (id: string) => void;
  onLoadMore: () => void;
};

const NotificationList = ({
  isLoading,
  items,
  grouped,
  hasNextPage,
  isFetchingNextPage,
  onOpen,
  onArchive,
  onLoadMore,
}: ListProps) => {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-20 animate-pulse rounded-xl border border-admin-border bg-admin-surface"
          />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-admin-border bg-admin-surface py-16">
        <TrayIcon size={48} weight="duotone" className="text-admin-text-muted" />
        <p className="text-sm text-admin-text-secondary">Aucune notification pour ce filtre.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {grouped.map(([day, dayItems]) => (
        <div key={day}>
          <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-admin-text-muted">
            {day}
          </p>
          <div className="overflow-hidden rounded-xl border border-admin-border bg-admin-surface">
            {dayItems.map((item, idx) => (
              <NotificationRow
                key={item.id}
                item={item}
                isFirst={idx === 0}
                onOpen={onOpen}
                onArchive={onArchive}
              />
            ))}
          </div>
        </div>
      ))}

      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            disabled={isFetchingNextPage}
            className="border-admin-border bg-admin-background text-admin-text-secondary"
            onClick={onLoadMore}
          >
            {isFetchingNextPage ? "Chargement…" : "Charger plus"}
          </Button>
        </div>
      )}
    </div>
  );
};

const NotificationRow = ({
  item,
  isFirst,
  onOpen,
  onArchive,
}: {
  item: NotificationItem;
  isFirst: boolean;
  onOpen: (item: NotificationItem) => void;
  onArchive: (id: string) => void;
}) => {
  const display = getEventDisplay(item.type);
  const Icon = display.icon;

  return (
    <div
      className={cn(
        "group flex cursor-pointer items-start gap-4 px-5 py-4 transition-colors hover:bg-admin-surface-hover",
        !isFirst && "border-t border-admin-border",
        !item.isRead && "bg-amber-500/[0.03]",
      )}
      onClick={() => onOpen(item)}
    >
      <span
        className={cn(
          "mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg",
          display.className,
        )}
      >
        <Icon size={18} weight="regular" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {!item.isRead && <span className="size-2 shrink-0 rounded-full bg-admin-primary" />}
          <p
            className={cn(
              "truncate text-sm",
              item.isRead
                ? "font-medium text-admin-text-secondary"
                : "font-semibold text-admin-text-primary",
            )}
          >
            {item.title ?? display.label}
          </p>
          <Badge className="hidden shrink-0 border-0 bg-admin-surface-hover text-admin-text-secondary sm:inline-flex">
            {display.label}
          </Badge>
        </div>
        {item.body && (
          <p className="mt-0.5 line-clamp-2 text-sm text-admin-text-secondary">{item.body}</p>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <span className="whitespace-nowrap text-xs text-admin-text-muted">
          {relativeTime(item.createdAt)}
        </span>
        <button
          className="text-xs text-admin-text-muted opacity-0 transition-opacity hover:text-admin-text-primary group-hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            onArchive(item.id);
          }}
        >
          Archiver
        </button>
      </div>
    </div>
  );
};
